import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { bulkUploadEmployees } from '../../redux/corporateEmployeeSlice';
import ToastService from '../../core/notifications/ToastService';

export const BulkUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.corporateEmployee);
  
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string>('');
  const [coinsPerEmployee, setCoinsPerEmployee] = useState<string>('1000');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setUploadError('Please upload a valid CSV file');
        return;
      }

      setCsvFile(file);
      setUploadError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setCsvData(text);
      };
      reader.readAsText(file);
    }
  };

  const handleDownloadTemplate = () => {
    const template = 'employee_id,name,email,phone,department,designation\nEMP-001,Ravi Kumar,ravi@company.com,9876543210,IT,Software Engineer\nEMP-002,Priya Singh,priya@company.com,9876543211,Finance,Accountant';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'employee_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!csvData) {
      setUploadError('Please upload a CSV file');
      return;
    }

    if (!coinsPerEmployee || parseInt(coinsPerEmployee) < 0) {
      setUploadError('Please enter a valid coins amount');
      return;
    }

    try {
      setUploadError(null);
      await dispatch(bulkUploadEmployees({
        csv_data: csvData,
        coins_per_employee: parseInt(coinsPerEmployee),
        expiry_date: expiryDate || undefined
      })).unwrap();

      ToastService.success('Employees uploaded successfully');
      navigate('/corporate/employees');
    } catch (err: any) {
      setUploadError(err || 'Failed to upload employees');
      ToastService.error(err || 'Failed to upload employees');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/corporate/employees')}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Bulk Upload Employees
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Upload multiple employees using CSV file
          </Typography>
        </Box>
      </Box>

      {(error || uploadError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || uploadError}
        </Alert>
      )}

      {/* Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Instructions
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="1. Download the CSV template below" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Fill in employee details (employee_id, name, email, phone, department, designation)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Upload the completed CSV file" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. Set coins per employee and expiry date (optional)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="5. Submit to add employees and distribute coins" />
            </ListItem>
          </List>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ mt: 2 }}
          >
            Download CSV Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* File Upload */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Upload CSV File
                </Typography>
                <Paper
                  sx={{
                    p: 4,
                    border: '2px dashed #ccc',
                    textAlign: 'center',
                    backgroundColor: '#f9f9f9',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                  onClick={() => document.getElementById('csv-upload')?.click()}
                >
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <UploadIcon sx={{ fontSize: 48, color: '#14B8A6', mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {csvFile ? csvFile.name : 'Click to upload CSV file'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Supported format: CSV
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Coin Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Coin Allocation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Coins Per Employee"
                      value={coinsPerEmployee}
                      onChange={(e) => setCoinsPerEmployee(e.target.value)}
                      inputProps={{ min: 0 }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Expiry Date (Optional)"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Preview */}
          {csvData && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Preview
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f9f9f9', maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {csvData.split('\n').slice(0, 10).join('\n')}
                      {csvData.split('\n').length > 10 && '\n...'}
                    </Typography>
                  </Paper>
                  <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                    Showing first 10 rows. Total rows: {csvData.split('\n').length - 1}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => navigate('/corporate/employees')}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !csvData}
                sx={{ backgroundColor: '#14B8A6' }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Upload Employees'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default BulkUploadPage;
